import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  buildMapleMarketApp,
  mapleMarketCategoryMeta,
  type MapleMarketApp,
} from '@/data/mapleMarketApps';
import { useProfile } from '@/hooks/useProfile';

const PRODUCT_SELECT = [
  'id',
  'sku',
  'name',
  'developer',
  'short_description',
  'long_description',
  'price_cents',
  'is_free',
  'category',
  'rating',
  'reviews',
  'tags',
  'size_text',
  'release_date',
  'featured',
  'version',
  'package_type',
  'hero_image_url',
  'thumbnail_url',
  'package_path',
  'includes_web_installer',
  'includes_desktop_installer',
  'includes_bare_metal_installer',
  'supported_mapleos_editions',
  'visibility_status',
  'publish_request_status',
  'publish_requested_at',
  'publish_requested_by',
  'publish_approved_at',
  'publish_approved_by',
  'publish_rejected_at',
  'publish_rejected_by',
  'publish_rejection_reason',
  'first_published_at',
  'has_been_published',
].join(', ');

export function useMarketplaceProducts() {
  const { profile } = useProfile();
  const [products, setProducts] = useState<MapleMarketApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSiteAdmin = (profile?.role || '').toLowerCase() === 'admin';

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);

    const { data, error: queryError } = await supabase
      .from('marketplace_products')
      .select(PRODUCT_SELECT)
      .order('featured', { ascending: false })
      .order('rating', { ascending: false })
      .order('name', { ascending: true });

    if (queryError) {
      setProducts([]);
      setError(queryError.message);
      setLoading(false);
      return;
    }

    setProducts((data ?? []).map((product) => buildMapleMarketApp(product as any)));
    setLoading(false);
  }, []);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  const storeProducts = useMemo(
    () => (isSiteAdmin ? products : products.filter((product) => product.visibilityStatus === 'published')),
    [isSiteAdmin, products]
  );

  const featuredProducts = useMemo(
    () => storeProducts.filter((product) => product.featured),
    [storeProducts]
  );

  const categories = useMemo(
    () =>
      mapleMarketCategoryMeta.map((category) => ({
        ...category,
        count: storeProducts.filter((product) => product.category === category.name).length,
      })),
    [storeProducts]
  );

  const accessibleProductMap = useMemo(
    () => new Map(products.map((product) => [product.id, product])),
    [products]
  );

  const storeProductMap = useMemo(
    () => new Map(storeProducts.map((product) => [product.id, product])),
    [storeProducts]
  );

  const getProductById = useCallback(
    (id: string) => accessibleProductMap.get(id),
    [accessibleProductMap]
  );

  const getStoreProductById = useCallback(
    (id: string) => storeProductMap.get(id),
    [storeProductMap]
  );

  const filterProductsByIds = useCallback(
    (productIds: Iterable<string>, includeAll = false) => {
      if (includeAll) {
        return products;
      }

      const idSet = new Set(productIds);
      return products.filter((product) => idSet.has(product.id));
    },
    [products]
  );

  const filterLiveProductsByIds = useCallback(
    (productIds: Iterable<string>, includeAll = false) => {
      if (includeAll) {
        return products;
      }

      const idSet = new Set(productIds);
      return products.filter((product) => idSet.has(product.id));
    },
    [products]
  );

  return {
    products,
    liveProducts: products,
    storeProducts,
    featuredProducts,
    categories,
    loading,
    error,
    reload: loadProducts,
    getProductById,
    getStoreProductById,
    filterProductsByIds,
    filterLiveProductsByIds,
  };
}
